export enum UserRole {
  BASIC = 'BASIC',
  PREMIUM = 'PREMIUM',
  ADMIN = 'ADMIN',
}

type UserInfo = {
  // Legacy boolean flags that may or may not be present
  is_admin?: boolean;
  is_premium?: boolean;
  terms_accepted?: boolean;
  // Preferred: OAuth-style scopes claim; can be array or space/comma delimited string
  scopes?: string[] | string;
  scope?: string;
  // JWT standard claims
  sub?: string; // Subject - the user ID this token represents
  exp?: number; // Token expiration timestamp (seconds since Unix epoch)
};

type AuthTokenInfo = {
  role: UserRole;
  hasAcceptedTerms: boolean;
  isAdmin: boolean;
  // List of community/org IDs that the user owns, derived from the JWT `org` claim
  ownedCommunityIds: string[];
  // List of community/org IDs where the user has editor permissions
  editorCommunityIds: string[];
  // List of community/org IDs where the user has check-in permissions
  checkInCommunityIds: string[];
};

// ---- Owned communities extraction (strict JWT shape) ----

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function extractOwnedCommunityIdsFromClaim(orgsClaim: unknown): string[] {
  // Only accept the canonical shape: org: { OWNER: string[] }
  if (!orgsClaim || typeof orgsClaim !== 'object' || Array.isArray(orgsClaim)) {
    return [];
  }
  const ownerArray = (orgsClaim as Record<string, unknown>).OWNER;
  if (!isStringArray(ownerArray)) return [];
  return ownerArray.filter((id) => id.length > 0);
}

function extractEditorCommunityIdsFromClaim(orgsClaim: unknown): string[] {
  // Extract from the canonical shape: org: { EDITOR: string[] }
  if (!orgsClaim || typeof orgsClaim !== 'object' || Array.isArray(orgsClaim)) {
    return [];
  }
  const editorArray = (orgsClaim as Record<string, unknown>).EDITOR;
  if (!isStringArray(editorArray)) return [];
  return editorArray.filter((id) => id.length > 0);
}

function extractCheckInCommunityIdsFromClaim(orgsClaim: unknown): string[] {
  // Extract from the canonical shape: org: { CHECK_IN: string[] }
  if (!orgsClaim || typeof orgsClaim !== 'object' || Array.isArray(orgsClaim)) {
    return [];
  }
  const checkInArray = (orgsClaim as Record<string, unknown>).CHECK_IN;
  if (!isStringArray(checkInArray)) return [];
  return checkInArray.filter((id) => id.length > 0);
}

/**
 * Parse JWT token once and extract both user ID and expiry status
 */
export function parseAuthTokenForCredentials(authToken: string): {
  userId?: string;
  isExpired: boolean;
} {
  try {
    const decoded = authToken
      .split('.')
      .map((piece) => atob(piece.replace(/_/g, '/').replace(/-/g, '+')));
    const userInfo = JSON.parse(decoded[1]) as UserInfo;

    // Check expiry
    let isExpired = true;
    if (userInfo.exp) {
      const currentTimeSeconds = Date.now() / 1000;
      isExpired = userInfo.exp <= currentTimeSeconds;
    }

    return {
      userId: userInfo.sub,
      isExpired,
    };
  } catch {
    return {
      isExpired: true,
    };
  }
}

// ---- Manager App Permission Helpers ----

/**
 * Check if user has owner permissions for the selected community
 */
export function isOwnerUser(
  ownedCommunityIds?: string[],
  selectedCommunityId?: string,
) {
  if (!selectedCommunityId) return false;
  return Boolean(ownedCommunityIds?.includes(selectedCommunityId));
}

/**
 * Check if user has editor permissions (has access but not owner)
 */
export function isEditorUser(
  ownedCommunityIds?: string[],
  editorCommunityIds?: string[],
  selectedCommunityId?: string,
) {
  if (!selectedCommunityId) return false;
  return Boolean(
    editorCommunityIds?.includes(selectedCommunityId) &&
      !ownedCommunityIds?.includes(selectedCommunityId),
  );
}

/**
 * Check if user has check-in permissions for the selected community
 */
export function isCheckInUser(
  checkInCommunityIds?: string[],
  selectedCommunityId?: string,
) {
  if (!selectedCommunityId) return false;
  return Boolean(checkInCommunityIds?.includes(selectedCommunityId));
}

/**
 * Get the user's role for a specific community
 * Returns 'OWNER', 'EDITOR', 'CHECK_IN', or null
 */
export function getCommunityRole(
  ownedCommunityIds?: string[],
  editorCommunityIds?: string[],
  checkInCommunityIds?: string[],
  selectedCommunityId?: string,
): 'OWNER' | 'EDITOR' | 'CHECK_IN' | null {
  if (!selectedCommunityId) return null;
  if (ownedCommunityIds?.includes(selectedCommunityId)) return 'OWNER';
  if (editorCommunityIds?.includes(selectedCommunityId)) return 'EDITOR';
  if (checkInCommunityIds?.includes(selectedCommunityId)) return 'CHECK_IN';
  return null;
}

export const parseAuthTokenForPermissions = (
  authToken: string,
): AuthTokenInfo => {
  try {
    const decoded = authToken
      .split('.')
      .map((piece) => atob(piece.replace(/_/g, '/').replace(/-/g, '+')));
    const userInfo = JSON.parse(decoded[1]) as UserInfo;

    const scopesField = userInfo.scopes ?? userInfo.scope;
    let scopesArray: string[] = [];
    if (typeof scopesField === 'string') {
      scopesArray = scopesField.split(/[ ,]+/).filter(Boolean);
    } else if (Array.isArray(scopesField)) {
      scopesArray = scopesField;
    }

    const isAdmin = scopesArray.includes('ADMIN') || Boolean(userInfo.is_admin);
    // Extract org/community IDs from `org` claim in the shape { OWNER: string[], EDITOR: string[], CHECK_IN: string[] }
    const rawOrg = (userInfo as unknown as Record<string, unknown>).org;
    const ownedCommunityIds = extractOwnedCommunityIdsFromClaim(rawOrg);
    const editorCommunityIds = extractEditorCommunityIdsFromClaim(rawOrg);
    const checkInCommunityIds = extractCheckInCommunityIdsFromClaim(rawOrg);
    return {
      role: getUserRole(userInfo),
      hasAcceptedTerms: Boolean(userInfo.terms_accepted),
      isAdmin,
      ownedCommunityIds,
      editorCommunityIds,
      checkInCommunityIds,
    };
  } catch (e) {
    console.error('Failed to parse auth token', e);
  }

  return {
    role: UserRole.BASIC,
    hasAcceptedTerms: false,
    isAdmin: false,
    ownedCommunityIds: [],
    editorCommunityIds: [],
    checkInCommunityIds: [],
  };
};

function getUserRole(info: UserInfo) {
  const scopesField = info.scopes ?? info.scope;
  let scopesArray: string[] = [];
  if (typeof scopesField === 'string') {
    scopesArray = scopesField.split(/[ ,]+/).filter(Boolean);
  } else if (Array.isArray(scopesField)) {
    scopesArray = scopesField;
  }

  if (scopesArray.includes('ADMIN') || info.is_admin) return UserRole.ADMIN;
  else if (info.is_premium) return UserRole.PREMIUM;

  return UserRole.BASIC;
}
